import { Injectable } from '@angular/core';
import {
    AzavistaAIAssistant,
    HostContext,
    HostContextEntity,
} from './azavista-ai-chatbot.types';
import {
    getViewEntityIdPairs,
    VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS,
    ViewNavigatorEntityModuleName,
    ViewNavigatorEntityServiceLibMethodRecordKey,
    ViewNavigatorEntityServiceLibMethodRecord,
} from './view-host.context.utils';
import { AzavistaApiService } from '@azavista/servicelib';

type EntityId = string & {};

type EntityModuleAndIdPair = `${ViewNavigatorEntityModuleName}___${EntityId}`;

type ViewEntityInput = {
    id: EntityModuleAndIdPair;
    hostContextEntity: HostContextEntity;
};

/**
 *
 */
@Injectable({
    providedIn: 'root',
})
export class ViewHostContextService {
    private _currentView: string | null = null;
    private _previousView: string | null = null;

    private _currentViewEntities = new Map<
        EntityModuleAndIdPair,
        HostContextEntity | null
    >();
    private _currentViewTimeoutInstance: NodeJS.Timeout | null = null;
    private _currentViewTimeoutDuration = 5000;

    private _entitiesCache = new Map<
        EntityModuleAndIdPair,
        HostContextEntity
    >();
    private _widget?: AzavistaAIAssistant;

    /**
     * Sets the current view and widget,
     * the app will wait for the current view's entities to be resolved by calls to `pushServiceLibMethodResult`.
     * Once all entities are resolved, the view will be updated.
     * Otherwise, the view will be updated after a timeout by retrieving the entities from the cache.
     */
    setView(view: string, widget: AzavistaAIAssistant | null): void {
        if (!widget) return;

        if (view === this._currentView || !view) return;

        this._previousView = this._currentView;
        this._widget = widget;
        this._currentView = view;

        if (this._currentViewTimeoutInstance) {
            this.pushMissingEntityItemsFromCache();
        };

        // clear the current view entities and add the view entity id pairs
        // also clear the current view timeout instance
        this._currentViewEntities.clear();
        this._clearCurrentViewTimeoutInstance();
        const viewEntityIdPairs = getViewEntityIdPairs(view);

        if (viewEntityIdPairs.length === 0) {
            this.setWidgetContext();
            return;
        };

        viewEntityIdPairs.forEach((entityIdPair) => {
            this._currentViewEntities.set(
                `${entityIdPair.module as ViewNavigatorEntityModuleName}___${entityIdPair.id}`,
                null,
            );
        });

        // set the current view timeout instance
        this._currentViewTimeoutInstance = setTimeout(() => {
            this.pushMissingEntityItemsFromCache();
            this._clearCurrentViewTimeoutInstance();
        }, this._currentViewTimeoutDuration);
    }

    private _clearCurrentViewTimeoutInstance() {
        if (this._currentViewTimeoutInstance) {
            clearTimeout(this._currentViewTimeoutInstance);
            this._currentViewTimeoutInstance = null;
            this.setWidgetContext();
        }
    }

    private setWidgetContext() {
        const context = {
            entities: Array.from(this._currentViewEntities.values()).filter(a => !!a ),
            view: this._currentView,
        };
        this._widget?.setContext(context);
    }

    pushServiceLibMethodResult<
        T extends ViewNavigatorEntityServiceLibMethodRecordKey,
    >(
        methodName: T,
        methodResult: Awaited<ReturnType<AzavistaApiService[T]>>,
    ): void {
        const recordItem = VIEW_NAVIGATOR_ENTITY_SERVICELIB_METHODS[methodName];
        const baseEntity = recordItem?.entityGetter?.(methodResult as any);

        if (baseEntity) {
            const entityInputs: ViewEntityInput[] = recordItem.modules.map(
                (module: ViewNavigatorEntityModuleName) => {
                    const hostContextEntity = {
                        ...baseEntity,
                        module,
                    } satisfies HostContextEntity;

                    const entityModuleAndIdPair =
                        `${module}___${hostContextEntity.id}` as const;

                    return {
                        id: entityModuleAndIdPair,
                        hostContextEntity,
                    };
                },
            );

            this.pushEntityItems(entityInputs);
        }
    }

    private pushEntityItems(items: Array<ViewEntityInput>) {
        if (!this._currentViewTimeoutInstance) return;

        items.forEach(({ id, hostContextEntity }) => {
            this._entitiesCache.set(id, hostContextEntity);
            if (this._currentViewEntities.has(id)) {
                this._currentViewEntities.set(id, hostContextEntity);
            }
        });
        const entitiesArray = Array.from(this._currentViewEntities.values());
        // if the required entities are fulfilled, set the context
        if (entitiesArray.every((item) => !!item)) {
            this._clearCurrentViewTimeoutInstance();
        }
    }

    private pushMissingEntityItemsFromCache() {
        const viewEntitiesFromCache: ViewEntityInput[] = [];
        for (const key of this._currentViewEntities.keys()) {
            if (!this._currentViewEntities.get(key)) {
                const hostContextEntity = this._entitiesCache.get(key);
                if (hostContextEntity) {
                    viewEntitiesFromCache.push({
                        id: key,
                        hostContextEntity: hostContextEntity!,
                    });
                }
            }
        }
        if (viewEntitiesFromCache.length === 0) return;
        this.pushEntityItems(viewEntitiesFromCache);
    }
}
